'use client';

/**
 * @file AuctionItem.tsx
 * @description 경매 아이템 카드 컴포넌트
 *
 * 증권 앱 스타일의 데이터 중심 경매 아이템 카드를 제공합니다.
 * Progress Bar를 활용한 시각적 가격 비교와 상태별 색상 구분을 지원합니다.
 *
 * @see {@link /app/list/page.tsx} - 사용되는 페이지
 */

import { useState, useEffect } from 'react';
import { ArrowDown, Sparkles, Eye, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils/shared/core-utils';
import { RealAuction } from '@/lib/core/types';
import { getPropertyTypeLabel } from '@/lib/domain/auction/utils/property-labels';

interface AuctionItemProps {
  /** 경매 데이터 */
  auction: RealAuction;
  /** 클릭 핸들러 */
  onClick?: (auction: RealAuction) => void;
  /** 조회수 (선택사항) */
  viewCount?: number;
}

/**
 * 경매 아이템 카드 컴포넌트
 *
 * Progress Bar를 활용한 시각적 가격 비교와 Hover 효과를 제공합니다.
 */
export function AuctionItem({ auction, onClick, viewCount }: AuctionItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [clientViewCount, setClientViewCount] = useState<number | null>(null);

  // Hydration mismatch 방지를 위해 클라이언트에서만 랜덤 viewCount 생성
  useEffect(() => {
    if (viewCount === undefined) {
      setClientViewCount(Math.floor(Math.random() * 1000) + 50);
    }
  }, [viewCount]);

  const displayViewCount = viewCount ?? clientViewCount;

  // 가격 계산
  const appraisedValue = auction.appraisedValue;
  const minimumBidPrice = auction.minimumBidPrice;
  const discountAmount = appraisedValue - minimumBidPrice;
  const discountRate = Math.round((discountAmount / appraisedValue) * 100);

  // Progress Bar 계산 (감정가 대비 최저입찰가 비율)
  const progressRatio = (appraisedValue - minimumBidPrice) / appraisedValue;
  const progressPercent = Math.round(progressRatio * 100);

  // D-Day 계산
  const getDDay = () => {
    if (!auction.auctionDate) return null;

    const auctionDate = new Date(auction.auctionDate);
    const today = new Date();
    const diffTime = auctionDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'D-Day';
    if (diffDays > 0) return `D-${diffDays}`;
    return `D+${Math.abs(diffDays)}`;
  };

  const dDay = getDDay();

  // 매각기일 지남 여부 확인
  const isAuctionEnded = () => {
    if (!auction.auctionDate) return false;
    const auctionDate = new Date(auction.auctionDate);
    const today = new Date();
    // 오늘 날짜의 00:00:00으로 설정하여 날짜만 비교
    today.setHours(0, 0, 0, 0);
    auctionDate.setHours(0, 0, 0, 0);
    return auctionDate.getTime() < today.getTime();
  };

  const auctionEnded = isAuctionEnded();

  // 상태별 스타일링
  const getStatusStyle = () => {
    if (auction.failureCount >= 2) {
      // 유찰 2회 이상: Red
      return {
        badge: 'bg-red-50 text-red-600 border-red-100',
        badgeIcon: ArrowDown,
        progressBar: 'bg-red-500',
        discountText: 'text-red-600 bg-red-50',
        metaText: 'text-red-500',
      };
    } else if (auction.failureCount === 0) {
      // 신건: Blue
      return {
        badge: 'bg-blue-50 text-blue-600 border-blue-100',
        badgeIcon: Sparkles,
        progressBar: 'bg-blue-500',
        discountText: 'text-blue-600 bg-blue-50',
        metaText: 'text-zinc-800',
      };
    } else {
      // 일반: Gray
      return {
        badge: 'bg-zinc-100 text-zinc-500 border-zinc-200',
        badgeIcon: null,
        progressBar: 'bg-red-500',
        discountText: 'text-red-600 bg-red-50',
        metaText: 'text-zinc-800',
      };
    }
  };

  const statusStyle = getStatusStyle();
  const BadgeIcon = statusStyle.badgeIcon;

  // 가격 포맷팅
  const formatPrice = (price: number) => {
    if (price >= 100000000) {
      return `${(price / 100000000).toFixed(1)}억`;
    }
    return `${Math.round(price / 10000)}만`;
  };

  // 면적 포맷팅
  const formatArea = (area: number | null) => {
    if (!area) return '';
    return `${area}㎡`;
  };

  return (
    <div
      className={cn(
        'bg-white dark:bg-zinc-900 rounded-2xl p-4 sm:p-6 border border-zinc-200 dark:border-zinc-700 cursor-pointer transition-all duration-200',
        isHovered && 'shadow-lg dark:shadow-zinc-800/50 transform -translate-y-1 border-zinc-300 dark:border-zinc-600'
      )}
      onClick={() => onClick?.(auction)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Mobile: Vertical Stack, Desktop: Horizontal Layout */}
      <div className="flex flex-col md:flex-row gap-4 sm:gap-5 md:gap-6 md:items-center">

        {/* Info Section */}
        <div className="flex-1 min-w-0">
          {/* Badges */}
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2 flex-wrap">
            <span className="bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 text-[10px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded border border-zinc-200 dark:border-zinc-700">
              {auction.caseNo}
            </span>
            {auction.failureCount > 0 && (
              <span className={cn(
                'text-[10px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded border flex items-center gap-0.5 sm:gap-1',
                statusStyle.badge
              )}>
                {BadgeIcon && <BadgeIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />}
                {auction.failureCount === 1 ? '유찰' : `${auction.failureCount}회 유찰`}
              </span>
            )}
            {auction.failureCount === 0 && (
              <span className={cn(
                'text-[10px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded border flex items-center gap-0.5 sm:gap-1',
                statusStyle.badge
              )}>
                <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                신건
              </span>
            )}
            {auction.propertyType && (
              <span className="bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 text-[10px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded border">
                {getPropertyTypeLabel(auction.propertyType)}
              </span>
            )}
            {auction.auctionDate && (
              <span className={cn(
                'text-[10px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded border flex items-center gap-0.5 sm:gap-1',
                auctionEnded
                  ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700'
                  : 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
              )}>
                {auctionEnded ? (
                  <>
                    <XCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    종료
                  </>
                ) : (
                  '진행중'
                )}
              </span>
            )}
          </div>

          {/* Title & Address */}
          <h3 className="text-lg sm:text-xl font-bold text-zinc-900 dark:text-zinc-100 truncate hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
            {auction.buildingName || auction.address || '주소 정보 없음'}
          </h3>
          <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 mt-0.5 sm:mt-1 truncate">
            {auction.buildingDetail && `${auction.buildingDetail}, `}
            {formatArea(auction.buildingArea)}
            {auction.address && ` (${auction.address})`}
          </p>
        </div>

        {/* Financial Section (Key Visual) */}
        <div className="w-full md:w-80 shrink-0">
          {/* Price Comparison */}
          <div className="flex justify-between items-end mb-1.5 sm:mb-2">
            <div className="text-left">
              <p className="text-[9px] sm:text-[10px] text-zinc-400 dark:text-zinc-500 font-medium">감정가</p>
              {discountRate > 0 && (
                <p className="text-xs sm:text-sm text-zinc-400 dark:text-zinc-500 line-through decoration-zinc-300 dark:decoration-zinc-600">
                  {formatPrice(appraisedValue)}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-[9px] sm:text-[10px] text-zinc-600 dark:text-zinc-300 font-bold mb-0.5">최저입찰가</p>
              <p className="text-xl sm:text-2xl font-black text-zinc-900 dark:text-zinc-100 font-mono tracking-tight">
                {formatPrice(minimumBidPrice)}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full h-2 sm:h-2.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden flex relative">
            <div
              className="h-full bg-zinc-300 dark:bg-zinc-600 rounded-l-full transition-all duration-300"
              style={{ width: `${100 - progressPercent}%` }}
            />
            <div
              className={cn(
                'h-full rounded-r-full transition-all duration-300',
                statusStyle.progressBar
              )}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Discount Info */}
          {discountRate > 0 && (
            <div className="flex justify-end mt-1 sm:mt-1.5">
              <span className={cn(
                'text-[10px] sm:text-xs font-bold px-1 sm:px-1.5 py-0.5 rounded flex items-center gap-0.5 sm:gap-1',
                statusStyle.discountText
              )}>
                ▼ {discountRate}% ({formatPrice(discountAmount)} ↓)
              </span>
            </div>
          )}

          {/* New Auction Badge */}
          {auction.failureCount === 0 && (
            <div className="flex justify-end mt-1 sm:mt-1.5">
              <span className={cn(
                'text-[10px] sm:text-xs font-bold px-1 sm:px-1.5 py-0.5 rounded flex items-center gap-0.5 sm:gap-1',
                statusStyle.discountText
              )}>
                100% (첫 입찰)
              </span>
            </div>
          )}
        </div>

        {/* Meta Section */}
        <div className="w-full md:w-auto md:pl-6 md:border-l border-zinc-100 dark:border-zinc-700 flex md:flex-col justify-between md:justify-start items-end md:items-start gap-3 sm:gap-4 md:gap-4 shrink-0">
          {/* D-Day */}
          {dDay && (
            <div className="text-right md:text-left">
              <p className="text-[9px] sm:text-[10px] text-zinc-400 dark:text-zinc-500 font-medium uppercase tracking-wider">D-Day</p>
              <p className={cn(
                'text-sm sm:text-base font-bold',
                dDay === 'D-Day' ? 'text-red-500 dark:text-red-400' : statusStyle.metaText
              )}>
                {dDay}
              </p>
            </div>
          )}

          {/* Views */}
          {displayViewCount !== null && (
            <div className="text-right md:text-left">
              <p className="text-[9px] sm:text-[10px] text-zinc-400 dark:text-zinc-500 font-medium uppercase tracking-wider flex items-center gap-0.5 sm:gap-1 justify-end md:justify-start">
                <Eye className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                Views
              </p>
              <p className="text-xs sm:text-sm font-medium text-zinc-600 dark:text-zinc-300">
                {displayViewCount.toLocaleString()}
              </p>
            </div>
          )}

          {/* Court Info */}
          {auction.courtOfficeName && (
            <div className="text-right md:text-left md:hidden">
              <p className="text-[9px] sm:text-[10px] text-zinc-400 dark:text-zinc-500 font-medium uppercase tracking-wider">법원</p>
              <p className="text-[10px] sm:text-xs font-medium text-zinc-500 dark:text-zinc-400 truncate max-w-24 sm:max-w-32">
                {auction.courtOfficeName}
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
